/**
 * Settings (docs/11 §3.9, was Account). Vehicle type matters because pickup
 * rules differ by mode at the same kerb — it changes which zones the feed
 * returns. Everything else is preference or privacy.
 */
import { useState } from 'react';
import { ChevronLeft, ChevronRight, Download, LogOut, Trash2, UserRound, MailWarning, ShieldCheck } from 'lucide-react';
import { api } from './api.js';
import { store, type Prefs, type MapsProvider } from './storage.js';
import { Button, Chip } from './ui.js';

const MODES: [string, string][] = [
  ['taxi', 'Taxi'], ['rideshare', 'Rideshare'], ['livery', 'Livery'], ['private_hire', 'Private hire'],
];
const MAPS: [MapsProvider, string][] = [['apple', 'Apple'], ['google', 'Google'], ['waze', 'Waze']];

export function SettingsScreen({ name, email, emailVerified, driverMode, prefs, onModeChange, onPrefsChange, onEditProfile, onBack, onSignedOut }: {
  name: string | null;
  email: string | null;
  emailVerified: boolean;
  driverMode: string;
  prefs: Prefs;
  onModeChange: (m: string) => void;
  onPrefsChange: (p: Prefs) => void;
  onEditProfile: () => void;
  onBack: () => void;
  onSignedOut: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [resent, setResent] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  async function resend() {
    setResent('sending');
    try { await api.resendVerification(); setResent('sent'); } catch { setResent('failed'); }
  }
  const [busy, setBusy] = useState(false);

  async function signOut(everywhere: boolean) {
    setBusy(true);
    try { if (everywhere) await api.signOutEverywhere(); else await api.logout(); } catch { /* best-effort */ }
    store.purgeAll();
    onSignedOut();
  }
  async function deleteAccount() {
    setBusy(true);
    try { await api.deleteAccount(); } catch { setBusy(false); return; }
    store.purgeAll();
    onSignedOut();
  }
  const setPref = <K extends keyof Prefs>(k: K, v: Prefs[K]) => {
    const p = { ...prefs, [k]: v }; store.prefs.set(p); onPrefsChange(p);
  };

  return (
    <div className="screen">
      <header className="hdr">
        <button className="back" onClick={onBack}><ChevronLeft size={24} aria-hidden="true" /> Tonight</button>
        <span className="hdr-spacer" />
      </header>
      <h1 className="title">Settings</h1>
      <p className="sub">{[name, email].filter(Boolean).join(' · ')}</p>
      {email && !emailVerified && (
        <div className="inset" role="status">
          <MailWarning size={18} aria-hidden="true" />
          <div className="zone">
            <b>Confirm your email</b>
            <span>It’s how you get back in if you forget your password. We sent a link to {email}.</span>
            <button className="btn secondary" style={{ marginTop: 8 }} disabled={resent === 'sending' || resent === 'sent'}
              onClick={() => void resend()}>
              {resent === 'sent' ? 'Link sent — check your inbox' : resent === 'failed' ? 'Couldn’t send — try again' : 'Send the link again'}
            </button>
          </div>
        </div>
      )}

      <div className="settings-group">
        <h3>Vehicle</h3>
        <div className="chips">
          {MODES.map(([v, label]) => (
            <Chip key={v} selected={driverMode === v} onClick={() => { store.mode.set(v); onModeChange(v); }}>{label}</Chip>
          ))}
        </div>
        <p className="small muted">Pickup rules differ by vehicle at the same venue — this changes which curbs you’re shown.</p>
      </div>

      <div className="settings-group">
        <h3>Maps app</h3>
        <div className="chips">
          {MAPS.map(([v, label]) => (
            <Chip key={v} selected={prefs.maps === v} onClick={() => setPref('maps', v)}>{label}</Chip>
          ))}
        </div>
      </div>

      <div className="settings-group">
        <h3>Distance</h3>
        <div className="chips">
          <Chip selected={prefs.units === 'mi'} onClick={() => setPref('units', 'mi')}>Miles</Chip>
          <Chip selected={prefs.units === 'km'} onClick={() => setPref('units', 'km')}>Kilometres</Chip>
        </div>
      </div>

      <div className="settings-group">
        <h3>Reminders</h3>
        <div className="chips">
          <Chip selected={prefs.reminders} onClick={() => setPref('reminders', true)}>On</Chip>
          <Chip selected={!prefs.reminders} onClick={() => setPref('reminders', false)}>Off</Chip>
        </div>
        <p className="small muted">A notification at leave-by while Surge is open.</p>
      </div>

      <div className="settings-group">
        <button className="settings-row" onClick={onEditProfile}>
          <UserRound size={22} aria-hidden="true" />
          <span className="main">Profile<span className="l2">Name, age band, how you drive</span></span>
          <ChevronRight size={20} aria-hidden="true" />
        </button>
        <a className="settings-row" href="/api/v1/privacy/export" target="_blank" rel="noopener" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Download size={22} aria-hidden="true" />
          <span className="main">Your data<span className="l2">Download everything Surge holds about you</span></span>
          <ChevronRight size={20} aria-hidden="true" />
        </a>
        <a className="settings-row" href="/privacy" target="_blank" rel="noopener" style={{ textDecoration: 'none', color: 'inherit' }}>
          <ShieldCheck size={22} aria-hidden="true" />
          <span className="main">Privacy policy<span className="l2">What Surge keeps and what it never collects</span></span>
          <ChevronRight size={20} aria-hidden="true" />
        </a>
        <button className="settings-row" disabled={busy} onClick={() => void signOut(false)}>
          <LogOut size={22} aria-hidden="true" />
          <span className="main">Sign out<span className="l2">This phone</span></span>
        </button>
        <button className="settings-row" disabled={busy} onClick={() => void signOut(true)}>
          <LogOut size={22} aria-hidden="true" />
          <span className="main">Sign out everywhere<span className="l2">All phones and browsers</span></span>
        </button>
        {confirmDelete ? (
          <div className="card" style={{ gap: 10 }}>
            <p className="sub">This permanently deletes your account and everything linked to it. There is no undo.</p>
            <div className="btn-row">
              <Button variant="danger-fill" busy={busy} onClick={() => void deleteAccount()}>Delete my account</Button>
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>Keep it</Button>
            </div>
          </div>
        ) : (
          <button className="settings-row danger" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={22} aria-hidden="true" />
            <span className="main">Delete account</span>
            <ChevronRight size={20} aria-hidden="true" />
          </button>
        )}
      </div>

      <p className="small muted" style={{ textAlign: 'center', marginTop: 'auto' }}>
        Surge · Place search © OpenStreetMap contributors · Map © OpenFreeMap
      </p>
    </div>
  );
}
